// @ts-strict-ignore
import { Button } from '@coast/schemas';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ActionSheet, ActionSheetOption } from '@shared/components/ActionSheet';
import { MBButton, MBButtonProps } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { ShowActionSheetFn, executeActions } from '@shared/components/blocks/lib/button/actions';
import { useToast } from '@shared/components/toast';
import { useCardDefinition, useCardTemplates } from '@shared/scenes/cards/hooks';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { useNavigator } from '@shared/util/navigation';

interface Props {
  button: Button;
  card: API.CardFragment;
}

function makeButtonProps(button: Button): MBButtonProps {
  return {
    leftIcon: button.icon,
    title: button.label,
    size: button.size || 'normal',
    theme: button.style === 'secondary' ? 'outline' : 'normal',
    style: { marginHorizontal: 4 }, // this spaces out buttons in the same row
  };
}

export function MBButtonBlockButton({ button, card }: Props) {
  const channel = useContext(CardChannelContext);
  const { cardDefinition } = useCardDefinition(channel?.id, channel?.workflowTemplateId);
  const templates = useCardTemplates(cardDefinition);
  const formContext = useCardFormContext();
  const toast = useToast();
  const navigator = useNavigator();

  const [menuOptions, setMenuOptions] = useState<ActionSheetOption[]>([]);
  const [isMenuOpen, openMenu, closeMenu] = useBoolean();
  const anchorName = getButtonAnchorName(button);

  const showActionSheet = useCallback<ShowActionSheetFn>(
    options => {
      setMenuOptions(options);
      openMenu();
    },
    [openMenu],
  );

  const onPress = useCallback(
    () =>
      executeActions(
        button.actions,
        card,
        channel.slug,
        formContext,
        toast,
        templates,
        navigator,
        showActionSheet,
      ),
    [button.actions, card, channel.slug, formContext, navigator, showActionSheet, templates, toast],
  );
  const props = useMemo(() => makeButtonProps(button), [button]);
  return (
    <>
      <View style={{ maxWidth: 320 }} flex>
        <View style={{ anchorName }}>
          <MBButton {...props} onPress={onPress} />
        </View>
      </View>
      {isMenuOpen && (
        <ActionSheet anchorName={anchorName} options={menuOptions} onClose={closeMenu} />
      )}
    </>
  );
}

function getButtonAnchorName(button: Button): string {
  return `--button-block-${button.label.toLowerCase().replace(/ /g, '-')}`;
}
